// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA NUBE · EL MOTOR DE LA COLA DE SUBIDAS (fase 2, paquete P3; docs/NUBE-F2-F4-ESPEC.md §2.6)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Puro: ni React ni el DOM. La vista es components/nube/ColaSubidas.jsx y la página (pages/Nube.jsx) le pasa la API.
//  Lo mide backend/routes/nube-cola-subidas.test.js (G5) ejecutándolo con una API de mentira.
//
//  POR ARCHIVO: POST /nube/subidas (con la fecha de modificación del ordenador y, si viene de una carpeta, su ruta)
//  → los trozos EN CRUDO, uno detrás de otro, del tamaño que dice el servidor (PUT /nube/subidas/:id/trozos/:n, sin el
//  corte de 120 s de services/api) → POST /nube/subidas/:id/cerrar → y se pregunta cada 2 s hasta «cerrada» o «error»
//  (el servidor monta el archivo en segundo plano). Dos archivos a la vez como mucho.
//
//  REINTENTOS, con espera creciente: un fallo de red, un 5xx (salvo 507), un 499 y un 429 (el 429 DEMASIADAS_SUBIDAS
//  espera a que acabe otra). NO se reintenta lo que no va a cambiar: 400, 401, 402, 403, 404, 409 de estado, 410, 413,
//  415 ni 507; se enseña el texto del servidor.
//
//  SEGUIR DESPUÉS DE CERRAR LA PESTAÑA: el servidor guarda cada subida a medias 24 horas (GET /nube/subidas). Si se
//  vuelve a elegir un archivo con el mismo nombre y tamaño para la misma carpeta, sigue por los trozos que faltan.
//
//  (1-oct-2026, B-1013) LA COLA YA NO ES DE LA PÁGINA: vive por encima de las rutas (utils/nubeSubidasGlobal.js) y la
//  pinta en toda la aplicación components/nube/SubidasGlobales.jsx. Por eso aquí hay además:
//   · PAUSAR Y SEGUIR (`pausar`, `reanudar`): se corta el trozo en vuelo y no se empieza ninguno; al seguir, cada archivo
//     pregunta al servidor qué trozos le faltan y sigue por ahí (lo ya subido no se repite);
//   · `cancelarTodo` (avisa al servidor de cada una) y `abandonar` (sin una sola petición: la sesión o la clínica ya no
//     son las de la cola, y una petición más saldría con la identidad equivocada);
//   · NO DUPLICAR: el mismo archivo para la misma carpeta que ya está en la cola no se añade otra vez (si había fallado,
//     se reintenta ese), y lo que está subiendo esta cola no sale también como «a medias».
//
//  SUBIR UNA CARPETA ENTERA: primero se crean las carpetas, de la de arriba a las hojas (si ya hay una con ese nombre
//  donde se puede subir, se usa esa, como Windows), y después se encolan los archivos. Se saltan los del sistema
//  (Thumbs.db, desktop.ini, .DS_Store, «._…», los «~$…» de Office, los accesos directos .lnk y lo que hay dentro de
//  $RECYCLE.BIN y System Volume Information) y se dice cuántos. Las carpetas vacías no llegan (el navegador no las da).
import { planDeCarpeta as planDeCarpetaSoltada } from './leerSoltado'
import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const A_LA_VEZ = 2
export const CADA_MS_ESTADO = 2000
export const MAX_INTENTOS = 6
export const NO_SE_REINTENTAN = Object.freeze([400, 401, 402, 403, 404, 409, 410, 413, 415, 507])
export const MAX_RUTA_ORIGEN = 1024
// Lo que todavía no ha terminado (cuenta para «hay subidas en marcha»): también lo que está en pausa.
export const SIN_TERMINAR = Object.freeze(['esperando', 'subiendo', 'cerrando', 'pausada'])

const estadoHttp = (e) => (e && e.response ? Number(e.response.status) : null)
const datoDe = (e) => (e && e.response && e.response.data) || {}

// ¿Vale la pena volver a intentarlo? Sin respuesta (la red), 5xx salvo 507, 499 y 429: sí. Lo demás, no.
export function sePuedeReintentar(e) {
  if (e && e.cancelada) return false
  const st = estadoHttp(e)
  if (st == null) return true
  if (NO_SE_REINTENTAN.includes(st)) return false
  return st === 429 || st === 499 || st >= 500
}

// 1 s, 2 s, 4 s… hasta 30 s, con un poco de variación (para que dos pestañas no reintenten a la vez).
export function esperaDe(intento, azar = Math.random) {
  const base = Math.min(30000, 1000 * Math.pow(2, Math.max(0, intento)))
  return base + Math.floor(azar() * 250)
}

export function textoDelFallo(e) {
  const d = datoDe(e)
  if (d.error) return d.error
  if (estadoHttp(e) == null) return i18nT('utils.nubeSubidas.se_ha_cortado_la_conexion', 'Se ha cortado la conexión.')
  return i18nT('utils.nubeSubidas.no_se_ha_podido_subir', 'No se ha podido subir.')
}

const nfc = (s) => String(s == null ? '' : s).normalize('NFC')

// El cuerpo de POST /nube/subidas para un archivo: su nombre, tamaño, tipo, la fecha de modificación del ordenador
// (File.lastModified, la que enseña Windows) y, si viene de una carpeta, su ruta relativa.
export function cuerpoDeApertura(tarea) {
  const f = tarea.file
  const cuerpo = { carpeta_id: tarea.carpetaId == null ? null : Number(tarea.carpetaId), nombre: nfc(tarea.nombre || f.name), bytes: Number(f.size) }
  if (f.type) cuerpo.mime = String(f.type).slice(0, 160)
  const lm = Number(f.lastModified)
  if (Number.isFinite(lm) && lm > 0) cuerpo.modificado_origen = new Date(lm).toISOString()
  if (tarea.ruta) {
    const r = nfc(tarea.ruta)
    cuerpo.ruta_origen = r.length > MAX_RUTA_ORIGEN ? '…' + r.slice(r.length - (MAX_RUTA_ORIGEN - 1)) : r
  }
  if (tarea.siExiste && tarea.siExiste !== 'renombrar') cuerpo.si_existe = tarea.siExiste
  return cuerpo
}

// El plan de una carpeta soltada o elegida (qué carpetas crear y qué archivos subir o saltar): vive en utils/leerSoltado,
// junto a lo que lee lo soltado, para que la pestaña Archivos de la ficha lo use sin cargar este motor, que habla con
// /api/nube (t516: con él dentro, la ficha del paciente parecía pedir cosas de la Nube; paginas-con-sus-permisos §2).
export const planDeCarpeta = planDeCarpetaSoltada

const esperarMs = (ms) => new Promise((ok) => setTimeout(ok, ms))

// Crea las carpetas del plan dentro de `destinoId` (null = la principal), de padre a hija. Si ya hay una visible con ese
// nombre donde se puede subir, se usa esa (se fusiona). Devuelve { ids: Map(ruta -> id), fallos: [{ ruta, motivo }] }.
export async function crearCarpetas({ api, destinoId, carpetas }) {
  const ids = new Map()
  const fallos = []
  const idDe = (ruta) => {
    const i = ruta.lastIndexOf('/')
    return i < 0 ? destinoId : ids.get(ruta.slice(0, i))
  }
  for (const ruta of carpetas) {
    const padre = idDe(ruta)
    const nombre = ruta.slice(ruta.lastIndexOf('/') + 1)
    if (padre === undefined) { fallos.push({ ruta, motivo: i18nT('utils.nubeSubidas.no_se_ha_podido_crear', 'No se ha podido crear la carpeta de encima.') }); continue }
    try {
      const r = await api.post('/nube/carpetas', { padre_id: padre == null ? null : Number(padre), nombre })
      ids.set(ruta, Number(r.data.carpeta.id))
    } catch (e) {
      if (estadoHttp(e) !== 409) { fallos.push({ ruta, motivo: textoDelFallo(e) }); continue }
      // Ya hay algo con ese nombre: si es una carpeta que se ve y admite subir, se usa esa.
      const ya = await buscarCarpeta(api, padre, nombre)
      if (ya && ya.acciones && ya.acciones.subir) ids.set(ruta, Number(ya.id))
      else fallos.push({ ruta, motivo: i18nT('utils.nubeSubidas.ya_hay_algo_con_ese', 'Ya hay algo con ese nombre donde no puedes subir.') })
    }
  }
  return { ids, fallos }
}

async function buscarCarpeta(api, padre, nombre) {
  const clave = nfc(nombre).toLowerCase()
  for (let pagina = 1; pagina <= 50; pagina++) {
    let d
    try {
      d = (await api.get(`/nube/carpetas/${padre == null ? 'raiz' : padre}`, { params: { solo: 'carpetas', por_pagina: 200, pagina } })).data
    } catch (_) { return null }
    const c = (d.carpetas || []).find((x) => nfc(x.nombre).toLowerCase() === clave)
    if (c) return c
    if (!d.carpetas || d.carpetas.length < 200) return null
  }
  return null
}

// ─── LA COLA ────────────────────────────────────────────────────────────────────────────────────
let _siguiente = 1

export function crearCola({ api, alCambiar = () => {}, aLaVez = A_LA_VEZ, esperar = esperarMs, cadaMsEstado = CADA_MS_ESTADO, ahora = () => Date.now(), azar = Math.random } = {}) {
  const tareas = []
  let pendientes = []        // las subidas a medias que dice el servidor (GET /nube/subidas)
  let activas = 0
  let ultimoAviso = 0
  let enPausa = false
  const PAUSA = () => Object.assign(new Error('en pausa'), { pausa: true })
  const avisar = (forzar) => {
    const t = ahora()
    if (!forzar && t - ultimoAviso < 250) return
    ultimoAviso = t
    try { alCambiar() } catch (_) { /* la vista no puede tumbar la cola */ }
  }

  async function conReintentos(t, fn) {
    for (let intento = 0; ; intento++) {
      if (t.cancelada) throw Object.assign(new Error('cancelada'), { cancelada: true })
      if (enPausa) throw PAUSA()
      try { return await fn() } catch (e) {
        if (t.cancelada || (e && e.cancelada)) throw Object.assign(new Error('cancelada'), { cancelada: true })
        if (enPausa) throw PAUSA()
        if (!sePuedeReintentar(e) || intento + 1 >= MAX_INTENTOS) throw e
        const demasiadas = estadoHttp(e) === 429
        t.aviso = demasiadas ? i18nT('utils.nubeSubidas.esperando_a_que_termine_otra', 'Esperando a que termine otra subida…') : i18nT('utils.nubeSubidas.reintentando', 'Reintentando…')
        avisar(true)
        await esperar(demasiadas ? Math.max(esperaDe(intento, azar), 5000) : esperaDe(intento, azar))
        t.aviso = null
      }
    }
  }

  async function subirUna(t) {
    t.estado = 'subiendo'
    t.error = null
    t.inicio = ahora()
    avisar(true)
    try {
      let faltan
      if (!t.sid) {
        const r = await conReintentos(t, () => api.post('/nube/subidas', cuerpoDeApertura(t)))
        if (r.data && r.data.saltado) { t.estado = 'saltada'; t.archivoId = r.data.archivo_id; return }
        t.sid = r.data.id
        t.trozoBytes = Number(r.data.trozo_bytes)
        t.trozosTotal = Number(r.data.trozos_total)
        faltan = Array.from({ length: t.trozosTotal }, (_, i) => i)
      } else {
        const e = (await conReintentos(t, () => api.get(`/nube/subidas/${t.sid}`))).data
        t.trozoBytes = Number(e.trozo_bytes)
        t.trozosTotal = Number(e.trozos_total)
        if (e.estado === 'cerrada') { t.estado = 'hecha'; t.archivoId = e.archivo_id; t.enviados = t.total; return }
        faltan = e.estado === 'cerrando' ? [] : (e.faltan || [])
        t.enviados = Math.max(0, t.total - faltan.reduce((s, n) => s + Math.max(0, Math.min(t.trozoBytes, t.total - n * t.trozoBytes)), 0))
      }
      t.desde = t.enviados
      t.inicio = ahora()
      for (const n of faltan) {
        const ini = n * t.trozoBytes
        const fin = Math.min(ini + t.trozoBytes, t.total)
        const trozo = t.file.slice(ini, fin)
        const antes = t.enviados
        await conReintentos(t, () => {
          t.control = typeof AbortController !== 'undefined' ? new AbortController() : null
          return api.put(`/nube/subidas/${t.sid}/trozos/${n}`, trozo, {
            headers: { 'Content-Type': 'application/octet-stream' }, timeout: 0,
            signal: t.control ? t.control.signal : undefined,
            onUploadProgress: (ev) => { t.enviados = antes + Math.min(fin - ini, Number(ev && ev.loaded) || 0); avisar(false) },
          })
        })
        t.enviados = antes + (fin - ini)
        avisar(false)
      }
      const c = await conReintentos(t, () => api.post(`/nube/subidas/${t.sid}/cerrar`))
      t.estado = 'cerrando'
      avisar(true)
      let e = c && c.data && c.data.estado === 'cerrada' ? c.data : null
      while (!e) {
        await esperar(cadaMsEstado)
        const s = (await conReintentos(t, () => api.get(`/nube/subidas/${t.sid}`))).data
        if (s.estado === 'cerrada') e = s
        else if (s.estado === 'error') throw Object.assign(new Error(s.error || 'No se ha podido guardar el archivo.'), { response: { status: 422, data: { error: s.error || 'No se ha podido guardar el archivo.' } } })
        else if (s.estado === 'cancelada' || s.estado === 'caducada') throw Object.assign(new Error('La subida ya no sigue.'), { response: { status: 410, data: { error: 'Esta subida ya no sigue (se canceló o pasó un día sin avanzar).' } } })
      }
      t.estado = 'hecha'
      t.archivoId = e.archivo_id == null ? null : Number(e.archivo_id)
      t.enviados = t.total
    } catch (e) {
      if (t.cancelada || (e && e.cancelada)) {
        t.estado = 'cancelada'
        // Cancelada mientras se abría: el servidor ya le había dado su subida y nadie se la había cancelado (se quedaba
        // reservando espacio 24 horas). No si la cola se ha abandonado: esa petición saldría con otra identidad.
        if (t.sid && !t.avisado && !t.abandonada) { t.avisado = true; Promise.resolve().then(() => api.delete(`/nube/subidas/${t.sid}`)).catch(() => {}) }
        return
      }
      // En pausa: se queda con su subida abierta (`sid`); al seguir, pregunta qué trozos faltan y sigue por ahí. Si ya se
      // ha pulsado «Seguir» mientras se cortaba, vuelve a la fila.
      if (e && e.pausa) { t.estado = enPausa ? 'pausada' : 'esperando'; t.aviso = null; return }
      t.estado = 'error'
      t.error = textoDelFallo(e)
      t.codigo = datoDe(e).codigo || null
      t.libre = datoDe(e).libre_bytes == null ? null : Number(datoDe(e).libre_bytes)
      // Una subida que el servidor ya no conoce (caducó, se canceló) se empieza de cero al reintentar.
      if ([404, 410].includes(estadoHttp(e))) t.sid = null
    } finally {
      t.control = null
    }
  }

  function bombear() {
    if (enPausa) return
    while (activas < aLaVez) {
      const t = tareas.find((x) => x.estado === 'esperando')
      if (!t) break
      activas++
      subirUna(t).finally(() => { activas--; avisar(true); bombear() })
    }
  }

  const cola = {
    // Lo que dice el servidor que quedó a medias (al entrar en la Nube).
    ponerPendientes(lista) { pendientes = Array.isArray(lista) ? lista.slice() : []; avisar(true) },
    // Lo que está subiendo ESTA cola no es «a medias de otra vez», aunque el servidor lo liste (sigue abierta).
    pendientes: () => { const mias = new Set(tareas.map((t) => t.sid).filter(Boolean)); return pendientes.filter((p) => !mias.has(p.id)) },
    // [{ file, carpetaId, carpetaNombre?, ruta?, siExiste?, nombre? }]
    anadir(lista) {
      const nuevas = []
      for (const x of lista || []) {
        // NO DUPLICAR: el mismo archivo (nombre, tamaño, fecha y ruta) para la misma carpeta que ya está en la cola.
        const nombreX = nfc(x.nombre || x.file.name)
        const carpetaX = x.carpetaId == null ? null : Number(x.carpetaId)
        const ya = tareas.find((o) => (SIN_TERMINAR.includes(o.estado) || o.estado === 'error') && o.nombre === nombreX && o.total === Number(x.file.size)
          && o.carpetaId === carpetaX && (o.ruta || null) === (x.ruta || null) && Number(o.file.lastModified) === Number(x.file.lastModified))
        if (ya) {
          if (ya.estado === 'error') { ya.file = x.file; ya.estado = 'esperando'; ya.error = null; ya.cancelada = false; nuevas.push(ya) }
          continue
        }
        const t = {
          id: _siguiente++, file: x.file, nombre: nfc(x.nombre || x.file.name), carpetaId: x.carpetaId == null ? null : Number(x.carpetaId),
          carpetaNombre: x.carpetaNombre || null, ruta: x.ruta || null, siExiste: x.siExiste || 'renombrar',
          total: Number(x.file.size), enviados: 0, estado: 'esperando', error: null, sid: null, aviso: null,
        }
        // ¿Es la misma que quedó a medias? (mismo nombre, tamaño y carpeta): sigue por donde iba.
        const i = pendientes.findIndex((p) => nfc(p.nombre) === t.nombre && Number(p.bytes_total) === t.total && Number(p.carpeta_id) === Number(t.carpetaId))
        if (i >= 0 && t.carpetaId != null) { t.sid = pendientes[i].id; pendientes.splice(i, 1) }
        tareas.push(t)
        nuevas.push(t)
      }
      avisar(true)
      bombear()
      return nuevas.map((t) => t.id)
    },
    async cancelar(id) {
      const t = tareas.find((x) => x.id === id)
      if (!t || ['hecha', 'cancelada', 'saltada'].includes(t.estado)) return
      t.cancelada = true
      if (t.control) { try { t.control.abort() } catch (_) { /* nada */ } }
      const sid = t.sid
      t.estado = 'cancelada'
      avisar(true)
      if (sid) { t.avisado = true; try { await api.delete(`/nube/subidas/${sid}`) } catch (_) { /* caducará sola en 24 h */ } }
    },
    async descartarPendiente(sid) {
      pendientes = pendientes.filter((p) => p.id !== sid)
      avisar(true)
      try { await api.delete(`/nube/subidas/${sid}`) } catch (_) { /* caducará sola */ }
    },
    reintentar(id) {
      const t = tareas.find((x) => x.id === id)
      if (!t || t.estado !== 'error') return
      t.estado = 'esperando'
      t.error = null
      t.cancelada = false
      avisar(true)
      bombear()
    },
    limpiarTerminadas() {
      for (let i = tareas.length - 1; i >= 0; i--) if (['hecha', 'cancelada', 'saltada'].includes(tareas[i].estado)) tareas.splice(i, 1)
      avisar(true)
    },
    // PAUSAR: no empieza ningún archivo más y corta el trozo que está en vuelo (lo ya subido se conserva).
    pausar() {
      if (enPausa) return
      enPausa = true
      for (const t of tareas) {
        if (t.estado === 'esperando') t.estado = 'pausada'
        else if ((t.estado === 'subiendo' || t.estado === 'cerrando') && t.control) { try { t.control.abort() } catch (_) { /* nada */ } }
      }
      avisar(true)
    },
    reanudar() {
      if (!enPausa) return
      enPausa = false
      for (const t of tareas) if (t.estado === 'pausada') t.estado = 'esperando'
      avisar(true)
      bombear()
    },
    pausada: () => enPausa,
    // CANCELAR TODO lo que no ha terminado, avisando al servidor de cada una (libera lo reservado). Devuelve cuántas.
    async cancelarTodo() {
      const vivas = tareas.filter((t) => SIN_TERMINAR.includes(t.estado))
      await Promise.all(vivas.map((t) => cola.cancelar(t.id)))
      return vivas.length
    },
    // ABANDONAR: la sesión o la clínica ya no son las de esta cola. Se corta todo SIN una sola petición (saldría con la
    // identidad equivocada); lo que quedó abierto en el servidor caduca solo en 24 horas. Devuelve cuántas estaban vivas.
    abandonar() {
      let n = 0
      for (const t of tareas) {
        if (!SIN_TERMINAR.includes(t.estado)) continue
        n++
        t.cancelada = true
        t.abandonada = true
        t.estado = 'cancelada'
        if (t.control) { try { t.control.abort() } catch (_) { /* nada */ } }
      }
      pendientes = []
      avisar(true)
      return n
    },
    quedaAlgo: () => tareas.some((t) => SIN_TERMINAR.includes(t.estado)),
    estado() {
      const lista = tareas.map((t) => ({
        id: t.id, nombre: t.nombre, carpetaNombre: t.carpetaNombre, estado: t.estado, total: t.total, enviados: t.enviados,
        error: t.error, codigo: t.codigo || null, libre: t.libre == null ? null : t.libre, aviso: t.aviso, archivoId: t.archivoId || null,
        velocidad: t.estado === 'subiendo' && t.inicio && ahora() > t.inicio ? Math.round((Math.max(0, t.enviados - (t.desde || 0)) * 1000) / (ahora() - t.inicio)) : 0,
      }))
      const vivas = lista.filter((t) => t.estado !== 'cancelada')
      return {
        tareas: lista,
        total: vivas.length,
        hechas: vivas.filter((t) => t.estado === 'hecha' || t.estado === 'saltada').length,
        bytesTotal: vivas.reduce((s, t) => s + t.total, 0),
        bytesHechos: vivas.reduce((s, t) => s + Math.min(t.total, t.enviados), 0),
        activas,
        pausada: enPausa,
        sinTerminar: vivas.filter((t) => SIN_TERMINAR.includes(t.estado)).length,
        errores: vivas.filter((t) => t.estado === 'error').length,
      }
    },
  }
  return cola
}
